import React, { useState } from 'react';
import PropTypes from 'prop-types';
import Portal from 'components/Portal';
import Button from 'components/Buttons/Button';
import EmptyStreet from 'assets/svg/empty_street.svg';
import { Avatar } from 'xland-ui';
import { useRect } from 'utils/customHooks';
import { literalTemplate } from 'utils/language';
import { Check } from 'lucide-react';
import { useSelector } from 'react-redux';
import './styles.scss';

function BaseSelect({
  actions,
  item = null,
  offset,
  items,
  renderVerifiedIcon,
  onChange,
  multiple = false,
  icon,
  text,
}) {
  const literals = useSelector(state => state.i18n.literals.startupsSelect);

  const [showOptions, setShowOptions] = useState(false);
  const [bbox, ref] = useRect(showOptions);

  const selectedItems = Array.isArray(item) ? item : [item];
  const selectedItemObjects = selectedItems.map(selectedItem => (typeof selectedItem === 'string'
    ? items.find(s => s.id === selectedItem)
    : selectedItem)).filter(Boolean);

  const allItems = [...items];
  selectedItemObjects.forEach(selectedItem => {
    if (!items.find(s => s.id === selectedItem?.id)) {
      allItems.push(selectedItem);
    }
  });

  const handleChangeItem = (selectedId) => {
    if (onChange) {
      if (allItems.length) {
        if (multiple) {
          const newSelectedIds = selectedItems.includes(selectedId)
            ? selectedItems.filter(id => id !== selectedId)
            : [...selectedItems, selectedId];
          onChange(newSelectedIds);
        } else {
          onChange(selectedId);
        }
      }
    }
  };

  const styles = { visible: true, zIndex: 12 };
  if (bbox) {
    styles.top = bbox.top + (offset?.top ?? 0);
    styles.left = bbox.left + bbox.width + (offset?.left ?? 0);
  }

  if (window.innerWidth < 600) {
    styles.top = 95;
    styles.left = 'auto';
    styles.right = 15;
  }

  return (
    <div
      ref={ref}
      className='nav-item'
      onClick={() => setShowOptions(!showOptions)}
    >
      <div className='left-menu-icon'>
        {!multiple ? (
          <Avatar
            title={selectedItemObjects[0].name}
            src={selectedItemObjects[0].logo?.path}
            size={20}
          />
        ) : (
          <>
            <span className='selected-count'>{selectedItemObjects.length}</span>
            {icon}
          </>
        )}
      </div>
      <p>{selectedItemObjects[0]?.name}</p>
      {renderVerifiedIcon && renderVerifiedIcon(selectedItemObjects[0].verified)}

      {showOptions && (
        <Portal styles={styles} onClose={() => setShowOptions(false)}>
          <div className='startups-select--wrapper'>
            {items.length > 0 ? (
              <>
                <p className='startups-select--title'>
                  {literals.selectItem}
                  {' '}
                  {text}
                </p>
                <div className='startups-select--items simple-scrollbar'>
                  {allItems.map(currentItem => (
                    <div
                      key={currentItem.id}
                      className='startups-select--item'
                      onClick={(e) => {
                        if (multiple) {
                          e.stopPropagation();
                        }
                        handleChangeItem(currentItem.id);
                      }}
                    >
                      <div className='startups-select--item-left'>
                        <Avatar
                          title={currentItem.name}
                          src={currentItem.logo?.path}
                          size={24}
                        />
                        {currentItem.name}
                        {renderVerifiedIcon && renderVerifiedIcon(currentItem.verified)}
                      </div>
                      {selectedItems.includes(currentItem.id) ? <Check size={20} /> : null}
                    </div>
                  ))}
                </div>
              </>
            ) : (
              <div className='panel-empty-message'>
                <img src={EmptyStreet} alt={literals.noItems} style={{ aspectRatio: '250/113' }} />
                <p>{literalTemplate(literals.noItems, { ITEM: text })}</p>
              </div>
            )}

            {actions.map(action => <Button key={action.text} {...action} />)}
          </div>
        </Portal>
      )}
    </div>
  );
}

BaseSelect.propTypes = {
  actions: PropTypes.array.isRequired,
  item: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.object,
    PropTypes.arrayOf(PropTypes.string),
    PropTypes.arrayOf(PropTypes.object),
  ]),
  offset: PropTypes.object,
  items: PropTypes.array.isRequired,
  renderVerifiedIcon: PropTypes.func,
  onChange: PropTypes.func.isRequired,
  multiple: PropTypes.bool,
  icon: PropTypes.node,
  text: PropTypes.string,
};

export default BaseSelect;
